import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { userRoles } from '../../data/mockData'
import api from '../../services/api'
import { Loader2 } from 'lucide-react'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Expense Management — real project expenses derived from milestone payments.
 * Data comes from GET /finance/expenses (paid milestones = cash outflows,
 * in-flight invoices = pending). Replaces the previous mock expense list.
 */
function ExpenseManagement() {
  const { t } = useTranslation('money')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedProject, setSelectedProject] = useState('all')
  const [selectedCategory, setSelectedCategory] = useState('all')

  useEffect(() => {
    api.get('/finance/expenses')
      .then(r => setExpenses(r.data || []))
      .catch(() => setExpenses([]))
      .finally(() => setLoading(false))
  }, [])

  const canManageExpenses = user?.role === userRoles.ADMIN ||
    user?.role === userRoles.FINANCE ||
    user?.role === userRoles.PROJECT_MANAGER

  // Distinct projects + categories for the filter dropdowns (from real data)
  const projectOptions = Array.from(
    new Map(expenses.map(e => [e.project_id, e.project_name])).entries()
  )
  const categoryOptions = Array.from(new Set(expenses.map(e => e.category)))

  const filteredExpenses = expenses.filter(e => {
    const projectMatch = selectedProject === 'all' || e.project_id === parseInt(selectedProject)
    const categoryMatch = selectedCategory === 'all' || e.category === selectedCategory
    return projectMatch && categoryMatch
  })

  const getStatusColor = (status) => ({
    Approved: 'bg-green-100 text-green-800',
    Paid: 'bg-blue-100 text-blue-800',
    Pending: 'bg-yellow-100 text-yellow-800',
    Rejected: 'bg-red-100 text-red-800',
  }[status] || 'bg-gray-100 text-gray-800')

  const totalExpenses = filteredExpenses.reduce((s, e) => s + e.amount, 0)
  const pendingExpenses = filteredExpenses.filter(e => e.status === 'Pending')
  const paidExpenses = filteredExpenses.filter(e => e.status === 'Paid')

  const expensesByCategory = filteredExpenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount
    return acc
  }, {})

  if (!canManageExpenses) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="p-12 text-center">
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('access_restricted')}</h3>
            <p className="mt-2 text-gray-600">{t('no_perm_expense')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-7 h-7 text-purple-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* header */}
      <div className="bg-linear-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('expense_management')}</h1>
            <p className="mt-1 opacity-90">{t('real_expenses')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{t('total_expenses')}</p>
            <p className="text-3xl font-bold">${totalExpenses.toLocaleString()}</p>
            <p className="text-sm opacity-90">{filteredExpenses.length} transactions</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('project_col')}</label>
            <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="form-input w-64">
              <option value="all">{t('all_projects')}</option>
              {projectOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tc('labels.category')}</label>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="form-input w-48">
              <option value="all">{t('all_categories')}</option>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-600">{t('total_expenses')}</p>
          <p className="text-2xl font-bold text-gray-900">${totalExpenses.toLocaleString()}</p>
          <p className="text-xs text-blue-600">{filteredExpenses.length} transactions</p>
        </div>
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-600">{t('pending_payment')}</p>
          <p className="text-2xl font-bold text-gray-900">${pendingExpenses.reduce((s, e) => s + e.amount, 0).toLocaleString()}</p>
          <p className="text-xs text-yellow-600">{pendingExpenses.length} items</p>
        </div>
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-600">Paid</p>
          <p className="text-2xl font-bold text-gray-900">${paidExpenses.reduce((s, e) => s + e.amount, 0).toLocaleString()}</p>
          <p className="text-xs text-green-600">{paidExpenses.length} items</p>
        </div>
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-600">{t('categories')}</p>
          <p className="text-2xl font-bold text-gray-900">{Object.keys(expensesByCategory).length}</p>
          <p className="text-xs text-purple-600">{t('active_categories')}</p>
        </div>
      </div>

      {/* Expense List */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('expense_transactions')}</h3>
        </div>
        <div className="overflow-x-auto">
          {filteredExpenses.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-sm">No expenses yet. Expenses appear here once milestone invoices are raised or paid.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Description', 'Project', 'Category', 'Amount', 'Date', 'Status'].map(h => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredExpenses.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{e.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{e.project_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{e.category}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${e.amount.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{e.date ? formatDate(e.date) : '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(e.status)}`}>{e.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(expensesByCategory).length > 0 && (
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('expense_by_category')}</h3>
          </div>
          <div className="p-6 space-y-4">
            {Object.entries(expensesByCategory).map(([category, amount]) => {
              const percentage = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0
              return (
                <div key={category}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-900">{category}</span>
                    <span className="text-sm text-gray-600">${amount.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{percentage.toFixed(1)}% of total</div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default ExpenseManagement
